import type { ComponentPropsWithRef } from "react";
import type { TaskUpdateBody } from "@api/task.api";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { fullDateFullYearFormat } from "@common/helpers/date";
import type { Task, TaskStatus } from "@common/types/task";
import type { User } from "@common/types/user";
import { TaskGroupSelect } from "@components/drawers/TaskGroupSelect";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { TaskPrioritySelect } from "@components/tasks/TaskPrioritySelect/TaskPrioritySelect";
import { TaskStatusSelect } from "@components/tasks/TaskStatusSelect/TaskStatusSelect";
import { TaskAssigneeCellPicker } from "@components/tasks/TaskView/Cells/TaskAssigneeCellPicker";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { useIsOverlayDeferred } from "@design-system/overlay/overlayDeferral";
import { Picker } from "@design-system/Picker/Picker.lib";
import { TimePicker } from "@design-system/TimePicker/TimePicker";
import { TaskServiceControl, TaskStageControl } from "./TaskBoardCloseControls";
import type { TaskCardEdit, TaskCardEditField } from "./taskCardEdits";
import type { DisableableTrigger } from "@design-system/overlay/triggerButtonProps";

/** Renders a control's trigger with the props the control hands it. */
export type RenderTaskChipTrigger = (
    props: ComponentPropsWithRef<"button">,
) => DisableableTrigger;

/** What a card's field control edits, and how. */
export interface TaskFieldEditor {
    task: Task;
    onEdit: (edit: TaskCardEdit) => void;
    /** Status changes go through the card's guard for finishing blocked tasks. */
    onStatusChange: (status: TaskStatus) => void;
}

interface TaskFieldControlProps {
    field: TaskCardEditField;
    editor: TaskFieldEditor;
    renderTrigger: RenderTaskChipTrigger;
}

const toTaskAssignee = ({
    id,
    name,
    firstName,
    lastName,
    email,
    avatar,
    iconColor,
}: User): NonNullable<Task["assignee"]> => ({
    id,
    name,
    firstName,
    lastName,
    email,
    avatar,
    iconColor,
});

/** List's assignee picker on `renderTrigger`, for a card or the open task's top bar. */
export const TaskAssigneeControl = ({
    editor: { task, onEdit },
    renderTrigger,
}: Readonly<{
    editor: Pick<TaskFieldEditor, "task" | "onEdit">;
    renderTrigger: RenderTaskChipTrigger;
}>) => {
    const { data: members = [] } = useCurrentWorkspaceMembers();
    const handleChange = (userId: User["id"] | null) => {
        const user = members.find((member) => member.user.id === userId)?.user;
        onEdit({
            task,
            body: { assigneeId: userId },
            assignee: userId === null ? null : user && toTaskAssignee(user),
        });
    };
    return (
        <TaskAssigneeCellPicker
            value={task.assigneeId ?? null}
            members={members}
            onValueChange={handleChange}
            trigger={<Picker.Trigger render={renderTrigger} />}
        />
    );
};

/**
 * The control List's column opens for `field`, on a card trigger: the same
 * pickers, values and updates. Entity edits follow List too, one update per
 * pick. While overlays are deferred it renders the trigger alone.
 */
export const TaskFieldControl = ({
    field,
    editor,
    renderTrigger,
}: Readonly<TaskFieldControlProps>) => {
    const isDeferred = useIsOverlayDeferred();
    if (isDeferred) {
        return renderTrigger({});
    }
    const { task, onEdit, onStatusChange } = editor;
    const edit = (body: TaskUpdateBody) => onEdit({ task, body });
    switch (field) {
        case "status":
            return (
                <TaskStatusSelect
                    value={task.status}
                    onValueChange={onStatusChange}
                    trigger={<Picker.Trigger render={renderTrigger} />}
                />
            );
        case "priority":
            return (
                <TaskPrioritySelect
                    value={task.priority}
                    onValueChange={(priority) => edit({ priority })}
                    trigger={<Picker.Trigger render={renderTrigger} />}
                />
            );
        case "group":
            return (
                <TaskGroupSelect
                    value={task.groupId}
                    onChange={(groupId) => edit({ groupId })}
                    trigger={<Picker.Trigger render={renderTrigger} />}
                />
            );
        case "assignee":
            return (
                <TaskAssigneeControl
                    editor={editor}
                    renderTrigger={renderTrigger}
                />
            );
        case "dueDate":
            return (
                <DatePicker
                    mode="day"
                    variant="panel-input"
                    clearable
                    showTriggerIcon={false}
                    displayFormat={fullDateFullYearFormat}
                    placeholder="Add due date"
                    value={task.dueDate ?? undefined}
                    onValueChange={(value) => edit({ dueDate: value ?? null })}
                    trigger={renderTrigger({})}
                />
            );
        case "estimate":
            return (
                <TimePicker
                    hideIcon
                    value={task.estimatedTime ?? undefined}
                    onChange={(value) => edit({ estimatedTime: value ?? null })}
                    trigger={renderTrigger({})}
                />
            );
        case "entities":
            return (
                <EntityFilterCombobox
                    filters={{ entityIds: task.entityIds ?? [] }}
                    onChange={({ entityIds }) =>
                        edit({ entityIds: entityIds ?? [] })
                    }
                    mode="selection"
                    renderTrigger={({ triggerProps }) =>
                        renderTrigger(triggerProps)
                    }
                />
            );
        case "stage":
            return (
                <TaskStageControl
                    editor={editor}
                    renderTrigger={renderTrigger}
                />
            );
        case "service":
            return (
                <TaskServiceControl
                    editor={editor}
                    renderTrigger={renderTrigger}
                />
            );
    }
};
